import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export type TreeListItem = {
  id: string;
  entityId: number;
  type: string;
  label: string;
  description?: string;
  enableAdd?: boolean;
  buttonAction?: () => void;
  options?: TreeListItem[];
  subcategoryParameters?: number[];
};

export type TreeListItemTsoPqProps = TreeListItem[];

type IgnoreRules = Readonly<Record<string, number>>;

export const defaultIgnoreRules: IgnoreRules = {
  '10minutes': 2,
  '2hours': 1,
};

type CategoryPathHolder = { categoryPath?: string | null };

export const defaultGetCategoryPath = (item: CategoryPathHolder) => item.categoryPath ?? '';

const splitCategoryPath = (path: string): string[] =>
  path
    .split('.')
    .map((segment) => segment.trim())
    .filter(Boolean);

const applyIgnoreRules = (segments: string[], rules: IgnoreRules): string[] => {
  let remaining = segments;
  while (remaining.length > 0) {
    const drop = rules[remaining[0]] ?? 0;
    if (!drop) break;
    remaining = remaining.slice(Math.min(drop, remaining.length));
  }
  return remaining;
};

type CategoryNode = {
  children: Map<string, CategoryNode>;
  itemsAtNode: CategoryPathHolder[];
};

const createCategoryNode = (): CategoryNode => ({ children: new Map(), itemsAtNode: [] });

const insertCategoryPath = (root: CategoryNode, segments: string[], item: CategoryPathHolder) => {
  let current = root;
  for (const segment of segments) {
    if (!current.children.has(segment)) {
      current.children.set(segment, createCategoryNode());
    }
    current = current.children.get(segment)!;
  }
  current.itemsAtNode.push(item);
};

const convertToLeafItem = (
  source: any,
  categoryKey: string,
  index: number,
  t: (key: string, opts?: any) => string
): TreeListItem => {
  const technicalName =
    (typeof source?.technicalName === 'string' && source.technicalName) || `${categoryKey}::${index}`;

  const translatedLabel = t(`measurementparameters.name.${technicalName}`);
  const label = `${translatedLabel} (${technicalName})`;

  const type = (typeof source?.type === 'string' && source.type) || 'item';

  return {
    id: technicalName,
    entityId: source.id,
    type,
    label,
  };
};

const buildGroupedTree = (
  nodeName: string,
  node: CategoryNode,
  parentSegments: string[],
  t: (key: string, opts?: any) => string,
  compare: (a: string, b: string) => number
): TreeListItem | null => {
  const fullSegments = [...parentSegments, nodeName];
  const categoryKey = fullSegments.join('.');

  const childItems = Array.from(node.children.entries())
    .sort(([a], [b]) => compare(a, b))
    .map(([childName, childNode]) => buildGroupedTree(childName, childNode, fullSegments, t, compare))
    .filter((n): n is TreeListItem => n !== null);

  const leafItems = node.itemsAtNode.map((item, index) => convertToLeafItem(item, categoryKey, index, t));

  const options = [...childItems, ...leafItems];
  if (options.length === 0) return null;

  return {
    id: categoryKey,
    entityId: 0,
    type: 'category',
    label: t(`measurementparameters.category.${nodeName}`),
    options,
  };
};

export function useLtreeTreeListFromObjects(
  items: CategoryPathHolder[] | null | undefined,
  getCategoryPath: (item: CategoryPathHolder) => string | null | undefined = defaultGetCategoryPath,
  ignoreRules: IgnoreRules = defaultIgnoreRules
): TreeListItemTsoPqProps {
  const { t } = useTranslation();

  const collator = useMemo(() => new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' }), []);

  return useMemo(() => {
    const root = createCategoryNode();

    for (const item of items ?? []) {
      const raw = getCategoryPath(item);
      if (typeof raw !== 'string' || raw.length === 0) continue;
      const segments = applyIgnoreRules(splitCategoryPath(raw), ignoreRules);
      if (segments.length === 0) continue;
      insertCategoryPath(root, segments, item);
    }

    const compare = collator.compare.bind(collator);

    return Array.from(root.children.entries())
      .sort(([a], [b]) => compare(a, b))
      .map(([rootName, child]) => buildGroupedTree(rootName, child, [], t, compare))
      .filter((n): n is TreeListItem => n !== null);
  }, [items, getCategoryPath, ignoreRules, t, collator]);
}
